import { LayoutType } from "@kalyzee/escaet-ui";
import { MeetingSessionOvenMediaReceiver } from "@kalyzee/kast-web-sdk";
import { useRefAutoUpdate } from "@kalyzee/kast-web-sdk/web-components";
import { useEffect, useMemo } from "react";
import { usePageContext } from "../../../../../../components/navigation/PageContext";
import { PedagogyHighlightContainer } from "../../../../../../components/pedagogy/containers/highlightContainer";
import { LoadingView } from "../../../../../../components/utils/LoadingView";
import { useMeetingContainerAPI, useMeetingSelector } from "../../../../../../hooks/meeting";
import { getExpertView, useActivityData, useExpertAvailableViews, usePopulatedMeetingData } from "../../../../../../hooks/populatedData";
import { SessionMode } from "../../../../../../store/session/slices";
import { usePedagogyContext } from "../../../context";
import styles from "./highlight.module.css";
import { getExpertAudioValidator } from "../../../../../../helpers/audio";
import { MeetingRoomStatus } from "@kalyzee/kast-web-sdk/websocket";

export const MeetingPedagogyInterventionHighlightPage = () => {
  const pageContext = usePageContext<SessionMode.MASTER>();
  const pageContextRef = useRefAutoUpdate(pageContext);
  const meetingContainerAPI = useMeetingContainerAPI(pageContextRef.current?.params?.id);
  const pedagogyCtx = usePedagogyContext();
  const pedagogyCtxRef = useRefAutoUpdate(pedagogyCtx);

  const [meeting] = useMeetingSelector(meetingContainerAPI, (state) => state.meeting);

  const populatedMeetingData = usePopulatedMeetingData(meetingContainerAPI);
  const activityData = useActivityData(populatedMeetingData);

  const availableExpertViews = useExpertAvailableViews(populatedMeetingData, activityData, pedagogyCtx?.spiedGroupId);
  const highlightView = useMemo(() => availableExpertViews?.find((v) => v.type === "highlight"), [availableExpertViews]);

  const { ongoingGroups } = useMemo(() => {
    const ongoing = populatedMeetingData?.rooms.groups.filter((group) => group.room.status === MeetingRoomStatus.Ongoing) ?? [];
    return { ongoingGroups: ongoing };
  }, [populatedMeetingData]);

  // ---------------------------------------------------------------------- //
  // ---------------------------   UTILS ----------------------------- //
  // ---------------------------------------------------------------------- //

  // ---------------------------------------------------------------------- //
  // ---------------------------   EFFECT ----------------------------- //
  // ---------------------------------------------------------------------- //

  useEffect(() => {
    pedagogyCtxRef.current?.setDisplayRemoteController(true); // display by default
  }, []);

  const view = useMemo(() => {
    return getExpertView("highlight", populatedMeetingData, activityData, pedagogyCtx?.spiedGroupId)
  }, [populatedMeetingData, activityData, pedagogyCtx?.spiedGroupId]);

  useEffect(() => {
    const audioValidator = getExpertAudioValidator(view);
    const validator = pedagogyCtxRef.current?.addAudioValidator(audioValidator);
    return () => {
      validator?.flush();
    };
  }, [view]);

  // ---------------------------------------------------------------------- //
  // ---------------------------   RENDER ----------------------------- //
  // ---------------------------------------------------------------------- //

  if (!meeting) return <LoadingView />;

  return (
    <div className={styles.container}>
      <PedagogyHighlightContainer
        meeting={meeting}
        ongoingGroups={ongoingGroups?.length ? true : false}
        meetingContainerAPI={meetingContainerAPI}
        context={pedagogyCtx}
        populatedMeetingData={populatedMeetingData}
        highlightView={highlightView}
        layoutType={pedagogyCtx?.layout ?? LayoutType.Square}
        disableHeader
        showEnableMicrophone={false} // already in the intervention toolbox
      />
    </div>
  );
};
